import { useState, useCallback, useMemo } from "react";
import { apiGet, fmtCurrency, fmtDate } from "@/utils/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { SupplierLookup } from "@/components/SupplierLookup";
import {
  Loader2,
  FileText,
  FileMinus,
  FilePlus2,
  RefreshCw,
  Search,
  ArrowUp,
  ArrowDown,
  ArrowUpDown,
  X,
} from "lucide-react";
import { toast } from "sonner";

// Doc-type visual mapping. SAP ByD codes:
//   004 = Supplier Invoice
//   005 = Supplier Credit Memo (qty/price variance credit — reduces payable)
//   006 = Subsequent Debit (price variance debit — adds to payable)
//   007 = Down Payment Request
// The SAP-provided text (``type_text``) is still shown in a tooltip because
// tenant labels vary slightly.
const DOC_META = {
  "004": { label: "Invoice", tone: "slate", icon: FileText },
  "005": { label: "Credit Memo", tone: "emerald", icon: FileMinus },
  "006": { label: "Subs. Debit", tone: "amber", icon: FilePlus2 },
  "007": { label: "Down Pmt.", tone: "indigo", icon: FileText },
};

const TONE_STYLES = {
  slate:   "bg-slate-100 text-slate-700 ring-slate-200",
  emerald: "bg-emerald-50 text-emerald-700 ring-emerald-200",
  amber:   "bg-amber-50 text-amber-800 ring-amber-200",
  indigo:  "bg-indigo-50 text-indigo-700 ring-indigo-200",
};

const DocTypeBadge = ({ typeCode, typeText }) => {
  const meta = DOC_META[typeCode] || { label: typeText || typeCode || "—", tone: "slate", icon: FileText };
  const Icon = meta.icon;
  return (
    <span
      title={typeText || meta.label}
      className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-wide ring-1 ${TONE_STYLES[meta.tone]}`}
      data-testid={`ledger-doc-type-${typeCode}`}
    >
      <Icon size={11} />
      {meta.label}
    </span>
  );
};

const StatusPill = ({ lifecycle, posting }) => {
  const label = posting && lifecycle ? `${lifecycle} · ${posting}` : lifecycle || posting || "—";
  const posted = String(posting || "").toLowerCase().includes("posted");
  return (
    <span
      className={`inline-block rounded px-2 py-0.5 text-[11px] font-medium ${posted ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-700"}`}
    >
      {label}
    </span>
  );
};

// Sort helpers ──────────────────────────────────────────────────────────────
const SORT_ACCESSORS = {
  date:          (d) => d.invoice_date || "",
  type:          (d) => (DOC_META[d.type_code]?.label || d.type_text || d.type_code || ""),
  sap_doc:       (d) => d.sap_invoice_id || "",
  supplier_doc:  (d) => d.supplier_doc_number || d.invoice_description || "",
  status:        (d) => `${d.lifecycle_status || ""} ${d.posting_status || ""}`.trim(),
  gross:         (d) => Number(d.gross_amount || 0),
  tax:           (d) => Number(d.tax_amount || 0),
  net:           (d) => Number(d.net_amount || 0),
};

const compareBy = (key, dir) => {
  const acc = SORT_ACCESSORS[key] || SORT_ACCESSORS.date;
  const sign = dir === "asc" ? 1 : -1;
  return (a, b) => {
    const va = acc(a);
    const vb = acc(b);
    if (typeof va === "number" && typeof vb === "number") return (va - vb) * sign;
    return String(va).localeCompare(String(vb), undefined, { numeric: true, sensitivity: "base" }) * sign;
  };
};

const SortableHeader = ({ label, sortKey, activeKey, dir, onSort, align = "left" }) => {
  const active = activeKey === sortKey;
  const Icon = active ? (dir === "asc" ? ArrowUp : ArrowDown) : ArrowUpDown;
  return (
    <th
      scope="col"
      className={`cursor-pointer select-none px-4 py-3 ${align === "right" ? "text-right" : "text-left"} ${active ? "text-slate-900" : "text-slate-500"} hover:text-slate-900`}
      onClick={() => onSort(sortKey)}
      data-testid={`ledger-sort-${sortKey}`}
    >
      <span className={`inline-flex items-center gap-1 ${align === "right" ? "justify-end" : ""}`}>
        {label}
        <Icon size={11} className={active ? "" : "opacity-50"} />
      </span>
    </th>
  );
};

export default function SupplierLedger() {
  const [vendor, setVendor] = useState(null);
  const [ledger, setLedger] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("all"); // all | 004 | 005 | 006 | 007
  const [sortKey, setSortKey] = useState("date");
  const [sortDir, setSortDir] = useState("desc");

  const fetchLedger = useCallback(async (vendorId, { force = false } = {}) => {
    if (!vendorId) return;
    setLoading(true);
    setError("");
    if (force) setLedger(null);
    try {
      const url = `/suppliers/${encodeURIComponent(vendorId)}/ledger${force ? "?refresh=true" : ""}`;
      const data = await apiGet(url);
      setLedger(data);
    } catch (e) {
      setError(e.message || "Failed to load supplier ledger");
      toast.error(e.message || "Failed to load supplier ledger");
    } finally {
      setLoading(false);
    }
  }, []);

  const onSelectVendor = (v) => {
    setVendor(v);
    setSearch("");
    setTypeFilter("all");
    setSortKey("date");
    setSortDir("desc");
    if (v?.id) fetchLedger(v.id);
    else setLedger(null);
  };

  const refresh = () => vendor?.id && fetchLedger(vendor.id, { force: true });

  const currency = ledger?.totals?.currencies?.[0] || "INR";

  const onSort = (key) => {
    if (sortKey === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      // Default: amounts and date descending; text ascending
      const defaultDesc = ["date", "gross", "tax", "net"].includes(key);
      setSortDir(defaultDesc ? "desc" : "asc");
    }
  };

  // Client-side filter + sort pipeline
  const visibleDocs = useMemo(() => {
    const docs = ledger?.documents || [];
    const q = search.trim().toLowerCase();
    const filtered = docs.filter((d) => {
      if (typeFilter !== "all" && d.type_code !== typeFilter) return false;
      if (!q) return true;
      const haystack = [
        d.sap_invoice_id,
        d.supplier_doc_number,
        d.invoice_description,
        d.type_text,
        d.lifecycle_status,
        d.posting_status,
        d.invoice_date,
        d.gross_amount != null ? String(d.gross_amount) : "",
      ]
        .map((v) => String(v || "").toLowerCase())
        .join(" ");
      return haystack.includes(q);
    });
    return filtered.sort(compareBy(sortKey, sortDir));
  }, [ledger, search, typeFilter, sortKey, sortDir]);

  const typeChips = useMemo(() => {
    const docs = ledger?.documents || [];
    const counts = { "004": 0, "005": 0, "006": 0, "007": 0 };
    docs.forEach((d) => {
      if (counts[d.type_code] !== undefined) counts[d.type_code] += 1;
    });
    return counts;
  }, [ledger]);

  return (
    <div className="flex min-w-0 flex-col gap-6" data-testid="supplier-ledger-page">
      {/* ── Supplier picker + header ── */}
      <section className="rounded-lg border border-slate-200 bg-white p-4">
        <div className="mb-3 flex items-center justify-between gap-3">
          <div>
            <h2 className="text-base font-semibold text-slate-900">Pick a supplier</h2>
            <p className="text-xs text-slate-500">
              Live SAP ByDesign supplier account — all invoices, credit memos, subsequent debit/credit documents.
            </p>
          </div>
          {ledger && (
            <Button
              variant="outline"
              size="sm"
              onClick={refresh}
              disabled={loading}
              data-testid="refresh-ledger-button"
            >
              <RefreshCw size={14} className={`mr-1.5 ${loading ? "animate-spin" : ""}`} />
              Refresh from SAP
            </Button>
          )}
        </div>
        <SupplierLookup value={vendor} onChange={onSelectVendor} />
      </section>

      {/* ── Loading / error ── */}
      {loading && (
        <div
          className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white p-6 text-sm text-slate-600"
          data-testid="ledger-loading"
        >
          <Loader2 size={16} className="animate-spin" />
          Loading supplier ledger from SAP…
        </div>
      )}
      {error && !loading && (
        <div
          role="alert"
          className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800"
          data-testid="ledger-error"
        >
          {error}
        </div>
      )}

      {/* ── Totals ── */}
      {ledger && !loading && (
        <>
          <section className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4" data-testid="ledger-totals">
            <TotalCard
              label="Total Invoices"
              value={fmtCurrency(Number(ledger.totals.invoice_total || 0), currency)}
              sub={`${typeChips["004"]} documents`}
              tone="slate"
              testId="total-invoices"
            />
            <TotalCard
              label="Total Credit Memos"
              value={fmtCurrency(Number(ledger.totals.credit_memo_total || 0), currency)}
              sub={`${typeChips["005"] + typeChips["006"]} documents`}
              tone="emerald"
              testId="total-credit-memos"
            />
            <TotalCard
              label="Net (Invoices − CMs)"
              value={fmtCurrency(Number(ledger.totals.net_balance || 0), currency)}
              sub="Gross payable (excludes payments)"
              tone="amber"
              testId="net-balance"
            />
            <TotalCard
              label="Documents"
              value={String(ledger.document_count)}
              sub={ledger.vendor_name || `Vendor ${ledger.vendor_id}`}
              tone="indigo"
              testId="doc-count"
            />
          </section>

          {/* ── Toolbar: search + type filter ── */}
          <section
            className="flex flex-col gap-3 rounded-lg border border-slate-200 bg-white p-3 sm:flex-row sm:items-center sm:justify-between"
            data-testid="ledger-toolbar"
          >
            <div className="relative w-full sm:max-w-md">
              <Search
                size={14}
                className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
              />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by SAP doc #, supplier doc #, status, amount…"
                className="h-9 pl-9 pr-9 text-sm"
                data-testid="ledger-search-input"
              />
              {search && (
                <button
                  type="button"
                  onClick={() => setSearch("")}
                  className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
                  aria-label="Clear search"
                  data-testid="ledger-search-clear"
                >
                  <X size={12} />
                </button>
              )}
            </div>
            <div className="flex flex-wrap items-center gap-1.5" data-testid="ledger-type-filter">
              {[
                ["all", `All (${ledger.documents.length})`],
                ["004", `Invoices (${typeChips["004"]})`],
                ["005", `Credit Memos (${typeChips["005"]})`],
                ["006", `Subs. Debit (${typeChips["006"]})`],
                ["007", `Down Pmt. (${typeChips["007"]})`],
              ].map(([key, label]) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => setTypeFilter(key)}
                  className={`rounded-full px-3 py-1 text-[11px] font-medium transition ${
                    typeFilter === key
                      ? "bg-slate-900 text-white"
                      : "bg-slate-100 text-slate-700 hover:bg-slate-200"
                  }`}
                  data-testid={`ledger-type-chip-${key}`}
                >
                  {label}
                </button>
              ))}
            </div>
          </section>

          {/* ── Doc list ── */}
          <section className="overflow-hidden rounded-lg border border-slate-200 bg-white">
            <header className="flex items-center justify-between border-b border-slate-200 bg-slate-50 px-4 py-2.5">
              <div className="flex items-baseline gap-2">
                <h3 className="text-sm font-semibold text-slate-900">
                  All documents · {ledger.vendor_name || ledger.vendor_id}
                </h3>
                <span className="text-xs text-slate-500" data-testid="ledger-visible-count">
                  ({visibleDocs.length}
                  {visibleDocs.length !== ledger.documents.length ? ` of ${ledger.documents.length}` : ""})
                </span>
              </div>
              <span className="text-[11px] text-slate-500" data-testid="ledger-source">
                {ledger.source}
                {ledger.cached ? " · cached" : " · live"}
              </span>
            </header>
            {visibleDocs.length === 0 ? (
              <p className="p-6 text-sm text-slate-500" data-testid="ledger-empty">
                {ledger.documents.length === 0
                  ? "No SAP documents found for this supplier."
                  : "No documents match your search / filter."}
              </p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-[13px]" data-testid="ledger-table">
                  <thead className="bg-slate-200 border-b-2 border-slate-300 text-[11px] font-bold uppercase tracking-wide text-slate-700">
                    <tr>
                      <SortableHeader label="Date"           sortKey="date"          activeKey={sortKey} dir={sortDir} onSort={onSort} />
                      <SortableHeader label="Type"           sortKey="type"          activeKey={sortKey} dir={sortDir} onSort={onSort} />
                      <SortableHeader label="SAP Doc #"      sortKey="sap_doc"       activeKey={sortKey} dir={sortDir} onSort={onSort} />
                      <SortableHeader label="Supplier Doc #" sortKey="supplier_doc"  activeKey={sortKey} dir={sortDir} onSort={onSort} />
                      <SortableHeader label="Status"         sortKey="status"        activeKey={sortKey} dir={sortDir} onSort={onSort} />
                      <SortableHeader label="Gross"          sortKey="gross"         activeKey={sortKey} dir={sortDir} onSort={onSort} align="right" />
                      <SortableHeader label="Tax"            sortKey="tax"           activeKey={sortKey} dir={sortDir} onSort={onSort} align="right" />
                      <SortableHeader label="Net"            sortKey="net"           activeKey={sortKey} dir={sortDir} onSort={onSort} align="right" />
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-300">
                    {visibleDocs.map((doc) => (
                      <tr
                        key={doc.sap_invoice_id}
                        className="hover:bg-slate-50"
                        data-testid={`ledger-row-${doc.sap_invoice_id}`}
                      >
                        <td className="whitespace-nowrap px-4 py-3 text-slate-700">
                          {fmtDate(doc.invoice_date)}
                        </td>
                        <td className="px-4 py-3">
                          <DocTypeBadge typeCode={doc.type_code} typeText={doc.type_text} />
                        </td>
                        <td className="whitespace-nowrap px-4 py-3 font-mono text-[13px] text-slate-900">
                          {doc.sap_invoice_id}
                        </td>
                        <td className="px-4 py-3 font-mono text-[13px] text-slate-800" data-testid={`ledger-supplier-doc-${doc.sap_invoice_id}`}>
                          {doc.supplier_doc_number || doc.invoice_description || <span className="text-slate-400">—</span>}
                        </td>
                        <td className="px-4 py-3">
                          <StatusPill lifecycle={doc.lifecycle_status} posting={doc.posting_status} />
                        </td>
                        <td className="whitespace-nowrap px-4 py-3 text-right font-medium text-slate-900">
                          {fmtCurrency(Number(doc.gross_amount || 0), doc.currency || currency)}
                        </td>
                        <td className="whitespace-nowrap px-4 py-3 text-right text-slate-600">
                          {fmtCurrency(Number(doc.tax_amount || 0), doc.currency || currency)}
                        </td>
                        <td className="whitespace-nowrap px-4 py-3 text-right text-slate-900">
                          {fmtCurrency(Number(doc.net_amount || 0), doc.currency || currency)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </>
      )}

      {!vendor && !loading && !error && (
        <p className="rounded-lg border border-dashed border-slate-300 bg-slate-50/50 p-8 text-center text-sm text-slate-500" data-testid="ledger-empty-state">
          Pick a supplier above to see their full SAP document ledger.
        </p>
      )}
    </div>
  );
}

const TotalCard = ({ label, value, sub, tone = "slate", testId }) => {
  const borderTone = {
    slate: "border-slate-200",
    emerald: "border-emerald-200",
    amber: "border-amber-200",
    indigo: "border-indigo-200",
  }[tone];
  return (
    <div
      className={`rounded-lg border ${borderTone} bg-white p-4`}
      data-testid={testId}
    >
      <p className="text-xs font-medium uppercase tracking-wide text-slate-500">{label}</p>
      <p className="mt-1 text-xl font-bold text-slate-900">{value}</p>
      <p className="mt-1 text-[11px] text-slate-500">{sub}</p>
    </div>
  );
};
